import { useAtom } from "jotai";
import { financialApplicationIdAtom } from "../atoms";
import { AppToasterV2 } from "@ucl/library";
import { FinancialApplicationCoreData } from "@/types";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";

interface FinancialApplicationModule {
  createFinancialApplication: () => Promise<FinancialApplicationCoreData | undefined>;
  ensureApplication: (autoCreate: boolean) => Promise<string | undefined>;
}

export const useFinancialApplication = (): FinancialApplicationModule => {
  const [financialApplicationId, setFinancialApplicationId] = useAtom(financialApplicationIdAtom);
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);

  const createFinancialApplication = async () => {
    try {
      // Call createFinancialApplication to create a new financial application
      const response = await financialApplicationApiClient.createFinancialApplication();
      return response;
    } catch (err) {
      console.error("createFinancialApplication - error:", err);
      AppToasterV2.showFailedActionToaster("Failed to initialize financial application");
    }
  };

  const ensureApplication = async (autoCreate: boolean = false) => {
    if (financialApplicationId) {
      // Validate exists in API
      try {
        const response =
          await financialApplicationApiClient.getFinancialApplicationCoreData(
            financialApplicationId
          );
        if (response) {
          return financialApplicationId;
        }
      } catch (err) {
        console.warn("getApplicationId (will go to auto create) - error:", err);
      }
    }

    let id: string | undefined;
    if (autoCreate) {
      const response = await createFinancialApplication();
      if (response) {
        id = response.id;
      }
    }

    if (!id) {
      AppToasterV2.showFailedActionToaster("Failed to initialize financial application.");
      return undefined;
    } else {
      setFinancialApplicationId(id);
      return id;
    }
  };

  return {
    createFinancialApplication,
    ensureApplication,
  };
};
